import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DayKey,
  DAYS_CONFIG,
  DEFAULT_WEEKLY_SCHEDULE,
  WeeklySchedule,
  TimeSlot,
  getBrowserTimezone,
  timeToMinutes,
  minutesToTime,
  doSlotsOverlap,
  validateDaySlots,
  isSlotConflicting,
  sortSlots,
} from './utils/timezoneUtils';
import { TimeSlotPicker } from './components/TimeSlotPicker';
import { TimezoneSelector } from './components/TimezoneSelector';
import { Modal } from 'antd';
import { ReactComponent as ProfileRemove2Icon } from 'src/assets/images/onboarding-trainer/dashboard/profile/components/profile-remove2.svg';
import { useSaveOnboardingStep } from 'src/api/mutations/useSaveOnboardingStep';
import { useUpdateTrainerSchedule } from 'src/api/mutations/useUpdateTrainerSchedule';
import { useGetTrainerSchedule } from 'src/api/queries/useGetTrainerSchedule';
import { ScheduleConflict } from 'src/api/mutations/useCheckScheduleConflicts';
import { sessionStore } from 'src/store/sessionStore';
import { getUserIdFromToken } from 'src/utils/authUtils';
import toast from 'src/utils/appAlert';
import './Schedule.scss';

/**
 * Maximum allowed time slots per day
 */
export const MAX_SLOTS_PER_DAY = 4;

/**
 * Rolling availability days ahead to sync checkin calendar slots (dynamic)
 */
export const SCHEDULE_SYNC_DAYS_AHEAD = 60;

/**
 * Default preset time slots when adding new slots sequentially (1 -> 2 -> 3 -> 4 slots)
 * Easily configurable here if default slot timings change.
 */
export const DEFAULT_SLOT_PROGRESSION: Record<number, { start: string; end: string }[]> = {
  1: [{ start: '09:00', end: '17:00' }],
  2: [
    { start: '09:00', end: '13:00' },
    { start: '13:00', end: '17:00' },
  ],
  3: [
    { start: '09:00', end: '12:00' },
    { start: '12:00', end: '14:30' },
    { start: '14:30', end: '17:00' },
  ],
  4: [
    { start: '09:00', end: '11:00' },
    { start: '11:00', end: '13:00' },
    { start: '13:00', end: '15:00' },
    { start: '15:00', end: '17:00' },
  ],
};

interface ScheduleProps {
  mode?: 'onboarding' | 'profile';
  onNext?: () => void;
  onBack?: () => void;
}

export const Schedule: React.FC<ScheduleProps> = ({ mode = 'onboarding', onNext, onBack }) => {
  const navigate = useNavigate();
  const token = sessionStore((s) => s.token);
  const userId = getUserIdFromToken(token);

  // Timezone state: auto-detect on mount
  const [timezone, setTimezone] = useState<string>(() => {
    const initialTz = getBrowserTimezone();
    console.log('[Schedule] Initial detected timezone state:', initialTz);
    return initialTz;
  });
  const [schedule, setSchedule] = useState<WeeklySchedule>(DEFAULT_WEEKLY_SCHEDULE);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [dayErrors, setDayErrors] = useState<Partial<Record<DayKey, string>>>({});

  // Confirmation modal state for disabling active days
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    dayKey: DayKey | null;
  }>({
    isOpen: false,
    dayKey: null,
  });

  // Booking conflict modal state when updating schedule
  const [conflictModal, setConflictModal] = useState<{
    isOpen: boolean;
    conflicts: ScheduleConflict[];
  }>({
    isOpen: false,
    conflicts: [],
  });

  // Queries and mutations
  const { data: existingScheduleData, isLoading: isScheduleLoading } = useGetTrainerSchedule(
    userId || undefined,
  );
  const { mutate: saveOnboardingStep, isPending: isSavingStep } = useSaveOnboardingStep();
  const { mutate: updateSchedule, isPending: isUpdatingSchedule } = useUpdateTrainerSchedule();

  const isPending = isSavingStep || isUpdatingSchedule;

  // Always reset scroll to top on mount or mode change
  useEffect(() => {
    const scrollableMain = document.getElementById('scrollableDashboardMain');
    if (scrollableMain) {
      scrollableMain.scrollTop = 0;
    }
    const scheduleContainer = document.querySelector('.schedule-page-container');
    if (scheduleContainer) {
      scheduleContainer.scrollTop = 0;
    }
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [mode]);

  // Hydrate from existing data if available
  useEffect(() => {
    console.log('[Schedule] existingScheduleData query state:', {
      isLoading: isScheduleLoading,
      hasData: Boolean(existingScheduleData?.data),
      backendTimezone: existingScheduleData?.data?.timezone,
      scheduleKeys: existingScheduleData?.data?.weekly_schedule
        ? Object.keys(existingScheduleData.data.weekly_schedule)
        : [],
    });

    if (existingScheduleData?.data) {
      if (existingScheduleData.data.timezone) {
        console.log(
          '[Schedule] Applying timezone from backend:',
          existingScheduleData.data.timezone,
        );
        setTimezone(existingScheduleData.data.timezone);
      } else {
        // If backend has empty timezone, ensure we have browser timezone
        const fallback = getBrowserTimezone();
        console.log('[Schedule] Backend timezone is empty, using browser fallback:', fallback);
        if (fallback) {
          setTimezone((current) => current || fallback);
        }
      }

      if (
        existingScheduleData.data.weekly_schedule &&
        Object.keys(existingScheduleData.data.weekly_schedule).length > 0
      ) {
        setSchedule({
          ...DEFAULT_WEEKLY_SCHEDULE,
          ...existingScheduleData.data.weekly_schedule,
        });
      }
    }
  }, [existingScheduleData, isScheduleLoading]);

  // Handle Day Toggle
  const handleToggleDay = (dayKey: DayKey) => {
    // Clear error for this day
    setDayErrors((prev) => {
      const next = { ...prev };
      delete next[dayKey];
      return next;
    });

    const currentDay = schedule?.[dayKey] || DEFAULT_WEEKLY_SCHEDULE[dayKey];
    if (currentDay.enabled) {
      // If turning off and day has slots, confirm first
      if (currentDay.slots && currentDay.slots.length > 0) {
        setConfirmModal({ isOpen: true, dayKey });
        return;
      }
      // If no slots, simply disable
      setSchedule((prev) => ({
        ...prev,
        [dayKey]: {
          ...(prev?.[dayKey] || DEFAULT_WEEKLY_SCHEDULE[dayKey]),
          enabled: false,
        },
      }));
    } else {
      // Enabling day: if empty, add default 09:00 - 17:00 slot
      const defaultSlots =
        currentDay.slots && currentDay.slots.length > 0
          ? currentDay.slots
          : [{ id: `slot-${dayKey}-1`, start: '09:00', end: '17:00' }];

      setSchedule((prev) => ({
        ...prev,
        [dayKey]: {
          enabled: true,
          slots: defaultSlots,
        },
      }));
    }
  };

  // Confirm disabling day
  const handleConfirmDisable = () => {
    if (confirmModal.dayKey) {
      const dayKey = confirmModal.dayKey;
      setDayErrors((prev) => {
        const next = { ...prev };
        delete next[dayKey];
        return next;
      });
      setSchedule((prev) => ({
        ...prev,
        [dayKey]: {
          enabled: false,
          slots: [],
        },
      }));
    }
    setConfirmModal({ isOpen: false, dayKey: null });
  };

  // Add slot to day (up to MAX_SLOTS_PER_DAY)
  const handleAddSlot = (dayKey: DayKey) => {
    const day = schedule[dayKey];
    if (day.slots.length >= MAX_SLOTS_PER_DAY) {
      setDayErrors((prev) => ({
        ...prev,
        [dayKey]: `Maximum ${MAX_SLOTS_PER_DAY} slots allowed per day.`,
      }));
      return;
    }

    const sorted = sortSlots(day.slots);
    const currentCount = sorted.length;
    const nextCount = currentCount + 1;
    const currentPattern = DEFAULT_SLOT_PROGRESSION[currentCount];
    const nextPattern = DEFAULT_SLOT_PROGRESSION[nextCount];

    // Check if existing slots match the default progression preset
    const isMatchingDefaultPattern =
      Boolean(currentPattern && nextPattern) &&
      sorted.every(
        (slot, idx) =>
          slot.start === currentPattern[idx]?.start && slot.end === currentPattern[idx]?.end,
      );

    let updatedSlots: TimeSlot[] = [];

    if (isMatchingDefaultPattern && nextPattern) {
      updatedSlots = nextPattern.map((slotDef, idx) => ({
        id: sorted[idx]?.id || `slot-${dayKey}-${Date.now()}-${idx + 1}`,
        start: slotDef.start,
        end: slotDef.end,
      }));
    } else {
      // Find next available gap or start immediately at end of last slot
      const lastSlot = sorted[sorted.length - 1];
      let newStart = '13:00';
      let newEnd = '17:00';

      if (lastSlot) {
        const lastEndMin = timeToMinutes(lastSlot.end);
        if (lastEndMin + 60 <= 1440) {
          newStart = minutesToTime(lastEndMin);
          newEnd = minutesToTime(Math.min(1440, lastEndMin + 240));
        } else {
          newStart = '08:00';
          newEnd = '12:00';
        }
      }

      // Verify it doesn't overlap
      const hasOverlap = sorted.some((s) => doSlotsOverlap(s.start, s.end, newStart, newEnd));

      if (hasOverlap) {
        // Find a non-overlapping 1-hour window
        let foundStart: number | null = null;
        for (let m = 0; m <= 1380; m += 30) {
          const testStart = minutesToTime(m);
          const testEnd = minutesToTime(m + 60);
          if (!sorted.some((s) => doSlotsOverlap(s.start, s.end, testStart, testEnd))) {
            foundStart = m;
            break;
          }
        }

        if (foundStart !== null) {
          newStart = minutesToTime(foundStart);
          newEnd = minutesToTime(foundStart + 60);
        } else {
          setDayErrors((prev) => ({
            ...prev,
            [dayKey]: 'No available non-overlapping time slot left on this day.',
          }));
          return;
        }
      }

      const newSlot: TimeSlot = {
        id: `slot-${dayKey}-${Date.now()}`,
        start: newStart,
        end: newEnd,
      };

      updatedSlots = sortSlots([...day.slots, newSlot]);
    }

    const err = validateDaySlots(updatedSlots);
    setDayErrors((prev) => {
      const next = { ...prev };
      if (err) {
        next[dayKey] = err;
      } else {
        delete next[dayKey];
      }
      return next;
    });

    setSchedule((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: updatedSlots,
      },
    }));
  };

  // Remove slot from day
  const handleRemoveSlot = (dayKey: DayKey, slotId: string) => {
    const day = schedule[dayKey];
    const updatedSlots = day.slots.filter((s) => s.id !== slotId);
    const err = validateDaySlots(updatedSlots);

    setDayErrors((prev) => {
      const next = { ...prev };
      if (err) {
        next[dayKey] = err;
      } else {
        delete next[dayKey];
      }
      return next;
    });

    setSchedule((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: updatedSlots,
      },
    }));
  };

  // Update slot time
  const handleSlotChange = (
    dayKey: DayKey,
    slotId: string,
    field: 'start' | 'end',
    newValue: string,
  ) => {
    const day = schedule[dayKey];
    const targetSlot = day.slots.find((s) => s.id === slotId);
    if (!targetSlot) return;

    const updatedStart = field === 'start' ? newValue : targetSlot.start;
    let updatedEnd = field === 'end' ? newValue : targetSlot.end;

    const startMins = timeToMinutes(updatedStart);
    let endMins = timeToMinutes(updatedEnd);

    // If start time moved to or past end time, bump end time by at least 1h (60m)
    if (field === 'start') {
      if (endMins <= startMins || endMins - startMins < 60) {
        endMins = Math.min(1440, startMins + 60);
        updatedEnd = minutesToTime(endMins);
      } else if (endMins - startMins > 480) {
        endMins = Math.min(1440, startMins + 480);
        updatedEnd = minutesToTime(endMins);
      }
    }

    const updatedSlots = day.slots.map((s) =>
      s.id === slotId ? { ...s, start: updatedStart, end: updatedEnd } : s,
    );

    const err = validateDaySlots(updatedSlots);

    setDayErrors((prev) => {
      const next = { ...prev };
      if (err) {
        next[dayKey] = err;
      } else {
        delete next[dayKey];
      }
      return next;
    });

    setSchedule((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: sortSlots(updatedSlots),
      },
    }));
  };

  // Direct save execution
  const executeSave = (force: boolean = false) => {
    const payload = {
      timezone,
      weekly_schedule: schedule,
      days: SCHEDULE_SYNC_DAYS_AHEAD,
    };

    if (mode === 'onboarding') {
      if (userId) {
        saveOnboardingStep(
          { uid: userId, step: 4, data: payload },
          {
            onSuccess: () => {
              if (onNext) {
                onNext();
              } else {
                navigate('/onboarding-trainer/dashboard');
              }
            },
            onError: (err: any) => {
              toast.error(err.message || 'Failed to save schedule');
            },
          },
        );
      } else {
        if (onNext) onNext();
      }
    } else {
      // Profile edit mode
      if (userId) {
        updateSchedule(
          {
            uid: userId,
            timezone,
            weekly_schedule: schedule,
            days: SCHEDULE_SYNC_DAYS_AHEAD,
            force,
          },
          {
            onSuccess: (res) => {
              if (res?.hasConflicts && res.conflicts && res.conflicts.length > 0) {
                setConflictModal({
                  isOpen: true,
                  conflicts: res.conflicts,
                });
                return;
              }

              toast.success('Availability schedule saved successfully.');
              if (onBack) {
                onBack();
              } else {
                navigate('/onboarding-trainer/dashboard/profile');
              }
            },
            onError: (err: any) => {
              toast.error(err.message || 'Failed to update schedule');
            },
          },
        );
      }
    }
  };

  // Confirm and proceed after reviewing conflicts
  const handleConfirmConflicts = () => {
    setConflictModal({ isOpen: false, conflicts: [] });
    executeSave(true);
  };

  // Save / Next handler (validates locally and calls unified API)
  const handleSave = () => {
    if (!timezone.trim()) {
      setErrorMsg('Please select a valid timezone to proceed.');
      return;
    }

    const errors: Partial<Record<DayKey, string>> = {};
    let hasAnyError = false;

    for (const dayConf of DAYS_CONFIG) {
      const dayData = schedule[dayConf.key];
      if (dayData?.enabled && dayData.slots?.length > 0) {
        const err = validateDaySlots(dayData.slots);
        if (err) {
          errors[dayConf.key] = err;
          hasAnyError = true;
        }
      }
    }

    if (hasAnyError || Object.keys(dayErrors).length > 0) {
      setDayErrors((prev) => ({ ...prev, ...errors }));
      return;
    }
    setErrorMsg('');

    executeSave(false);
  };

  return (
    <div className={`schedule-page-container ${mode}`}>
      <div className="schedule-header">
        <h1 className="schedule-title">
          {mode === 'profile' ? 'Edit Availability Schedule' : 'Let’s set your schedule'}
        </h1>
        <p className="schedule-subtitle">Choose when clients can book a check-in or message you.</p>
      </div>

      <div className="schedule-card">
        {/* Timezone Row */}
        <div className="schedule-card-header">
          <div className="timezone-label-group">
            <svg
              className="globe-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span className="label-text">Time zone</span>
          </div>

          <TimezoneSelector
            value={timezone}
            onChange={(newTz) => {
              setTimezone(newTz);
              if (errorMsg) setErrorMsg('');
            }}
            error={errorMsg}
          />
        </div>

        {/* Days List */}
        <div className="days-schedule-list">
          {DAYS_CONFIG.map(({ key, label }) => {
            const dayData = schedule?.[key] || DEFAULT_WEEKLY_SCHEDULE[key];
            const isEnabled = !!dayData?.enabled;
            const slots = dayData?.slots || [];
            const canAddSlot = isEnabled && slots.length < MAX_SLOTS_PER_DAY;
            const currentDayError = dayErrors[key];

            return (
              <div key={key} className="day-item-wrapper">
                <div
                  className={`day-row ${!isEnabled ? 'disabled-day' : ''} ${
                    currentDayError ? 'has-error' : ''
                  }`}
                >
                  {/* Day Name & Toggle */}
                  <div className="day-info-col">
                    <span className="day-name">{label}</span>
                    <button
                      type="button"
                      className={`day-toggle-switch ${isEnabled ? 'checked' : ''}`}
                      onClick={() => handleToggleDay(key)}
                      role="switch"
                      aria-checked={isEnabled}
                      aria-label={`Toggle availability for ${label}`}
                    >
                      <span className="toggle-thumb" />
                    </button>
                  </div>

                  {/* Day Slots or Unavailable */}
                  <div className="day-slots-col">
                    {!isEnabled ? (
                      <span className="unavailable-text">Unavailable</span>
                    ) : (
                      <>
                        <div className="slots-wrapper">
                          {slots.map((slot) => {
                            const startMin = timeToMinutes(slot.start);
                            const minEnd = minutesToTime(Math.min(1440, startMin + 60));
                            const maxEnd = minutesToTime(Math.min(1440, startMin + 480));
                            const isSlotInvalid = isSlotConflicting(slot, slots);

                            return (
                              <div
                                key={slot.id}
                                className={`single-slot-box ${isSlotInvalid ? 'has-error' : ''}`}
                              >
                                {/* Start Time 30-min Picker */}
                                <TimeSlotPicker
                                  value={slot.start}
                                  type="start"
                                  onChange={(newStart) =>
                                    handleSlotChange(key, slot.id, 'start', newStart)
                                  }
                                />

                                <span className="slot-separator">—</span>

                                {/* End Time 30-min Picker */}
                                <TimeSlotPicker
                                  value={slot.end}
                                  type="end"
                                  minTime={minEnd}
                                  maxTime={maxEnd}
                                  onChange={(newEnd) =>
                                    handleSlotChange(key, slot.id, 'end', newEnd)
                                  }
                                />

                                {/* Remove slot button if more than 1 slot */}
                                {slots.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn-remove-slot"
                                    onClick={() => handleRemoveSlot(key, slot.id)}
                                    aria-label="Remove slot"
                                    title="Remove slot"
                                  >
                                    &times;
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Add Slot Button (+) pinned to right side */}
                        <button
                          type="button"
                          className={`btn-add-slot ${!canAddSlot ? 'disabled' : ''}`}
                          onClick={() => canAddSlot && handleAddSlot(key)}
                          disabled={!canAddSlot}
                          aria-label={`Add time slot for ${label}`}
                          title={
                            slots.length >= MAX_SLOTS_PER_DAY
                              ? `Maximum ${MAX_SLOTS_PER_DAY} slots per day`
                              : 'Add new slot'
                          }
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Inline Error for this Day */}
                {currentDayError && (
                  <div className="day-inline-error" role="alert">
                    <svg
                      className="error-icon"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{currentDayError}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer Navigation */}
      {mode === 'onboarding' ? (
        <div className="schedule-footer">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: '100%' }} />
          </div>

          <div className="footer-actions">
            <button type="button" className="btn-back" onClick={onBack} disabled={isPending}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              <span>Back</span>
            </button>

            <button
              type="button"
              className="btn-next"
              onClick={handleSave}
              disabled={isPending || isScheduleLoading}
            >
              <span>{isPending ? 'Saving...' : 'Next'}</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="schedule-profile-footer">
          <button
            type="button"
            className="btn-profile-back"
            onClick={onBack ? onBack : () => navigate('/onboarding-trainer/dashboard/profile')}
            disabled={isPending}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            <span>Back to Profile</span>
          </button>

          <button
            type="button"
            className="btn-profile-save"
            onClick={handleSave}
            disabled={isPending || isScheduleLoading}
          >
            <span>{isPending ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      )}

      {/* Confirmation Modal for Day Disable */}
      <Modal
        open={confirmModal.isOpen}
        footer={null}
        onCancel={() => setConfirmModal({ isOpen: false, dayKey: null })}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">
            <ProfileRemove2Icon />
          </div>
          <h2>
            Disable{' '}
            {confirmModal.dayKey
              ? DAYS_CONFIG.find((d) => d.key === confirmModal.dayKey)?.label
              : ''}{' '}
            availability?
          </h2>
          <p>
            This will clear all configured time slots for this day. Are you sure you want to
            proceed?
          </p>
          <div className="logout-actions">
            <button className="btn-logout" onClick={handleConfirmDisable}>
              Disable &amp; Clear
            </button>
            <button
              className="btn-cancel"
              onClick={() => setConfirmModal({ isOpen: false, dayKey: null })}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal for Existing Booking Conflicts */}
      <Modal
        open={conflictModal.isOpen}
        footer={null}
        onCancel={() => setConflictModal({ isOpen: false, conflicts: [] })}
        centered
        width={440}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">
            <ProfileRemove2Icon />
          </div>
          <h2>Existing Bookings Affected</h2>
          <div style={{ textAlign: 'left', marginTop: '12px', width: '100%' }}>
            <p
              style={{
                marginBottom: '10px',
                color: '#94a3b8',
                fontSize: '13px',
                textAlign: 'center',
              }}
            >
              The following {conflictModal.conflicts.length} client booking(s) in the next 60 days
              fall outside your new schedule hours and will be removed:
            </p>
            <div
              style={{
                maxHeight: '160px',
                overflowY: 'auto',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                padding: '10px 14px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {conflictModal.conflicts.map((c, idx) => (
                <div
                  key={`${c.date}-${c.time}-${idx}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '13px',
                    color: '#ffffff',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>
                    📅 {c.day}, {c.date} at {c.time}
                  </span>
                  {c.clientName && (
                    <span style={{ color: '#f59e0b', fontSize: '12px' }}>({c.clientName})</span>
                  )}
                </div>
              ))}
            </div>
            <p
              style={{ marginTop: '12px', fontSize: '12px', color: '#F16A6A', textAlign: 'center' }}
            >
              Are you sure you want to proceed and update your schedule?
            </p>
          </div>
          <div className="logout-actions">
            <button className="btn-logout" onClick={handleConfirmConflicts}>
              Confirm &amp; Update
            </button>
            <button
              className="btn-cancel"
              onClick={() => setConflictModal({ isOpen: false, conflicts: [] })}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
